import React from "react";
import {
  Card,
  Elevation,
  Tag,
  Button,
  H3,
  H4,
  Text,
  FormGroup,
  InputGroup,
  HTMLSelect,
  Switch,
  TextArea,
  Tooltip,
  Position,
  Dialog,
  Classes,
  Callout,
  ControlGroup,
  Spinner,
  Icon,
} from "@blueprintjs/core";
import { DateInput, TimePrecision } from "@blueprintjs/datetime";
import moment from "moment";
import { inject, observer } from "mobx-react";
import { apiCallPost, apiCallPatch } from "../../../../micro/fns/util";
import ItemSelectionDropdown from "../../../components/ItemSelectionDropdown";
import SurgeryCalendar from "../../../components/calendar/SurgeryCalendar";
import cogoToast from "cogo-toast";
import { buildProcedureDischargeSummaryName } from "../../../utils/procedureSummary";
import { filterBookingsForSlotMetrics } from "../../../utils/surgerySlotUtils";
import SingleSelectWrapper from "../../../../micro/utils/SingleSelectWrapper";

class SxBookingDetailsTab extends React.Component {
  state = {
    // Item selection for clinical details
    selectedClinicalItems: [],
    // Track if clinical items have been modified
    isClinicalItemsDirty: false,
    // Calendar modal state
    isCalendarModalOpen: false,
    selectedCalendarDate: null,
    selectedCalendarSlot: null,
    chosenTime: null,
    pendingSurgeonChange: null, // Store surgeon ID when opening calendar
    calendarKey: 0, // Force calendar refresh on each open
    // Remove surgery confirmation state
    isRemoveSurgeryDialogOpen: false,
    surgeryToRemoveIndex: null,
    surgeryToRemove: null,
    isRemovingSurgery: false,
    showPostponeConfirm: false,
    postponeNotes: "",
    postponeNotesError: "",
    payorPanelNameItems: [],
    isLoadingPayorPanelNames: false,
  };
  openPostponeConfirm = () => {
    this.setState({
      showPostponeConfirm: true,
      postponeNotes: "",
      postponeNotesError: "",
    });
  };

  closePostponeConfirm = () => {
    this.setState({
      showPostponeConfirm: false,
      postponeNotes: "",
      postponeNotesError: "",
    });
  };

  handlePostponeNotesChange = (event) => {
    const value = event?.target?.value || "";
    this.setState({
      postponeNotes: value,
      postponeNotesError: "",
    });
  };

  confirmPostponeSurgery = async () => {
    const trimmedNotes = (this.state.postponeNotes || "").trim();
    const maxLength = 2000;

    if (!trimmedNotes) {
      this.setState({
        postponeNotesError: "Postponement notes are required.",
      });
      return;
    }

    if (trimmedNotes.length > maxLength) {
      this.setState({
        postponeNotesError: `Please keep notes under ${maxLength} characters.`,
      });
      return;
    }

    try {
      if (typeof this.props.onPostponeSurgery === "function") {
        await this.props.onPostponeSurgery({ postponementNotes: trimmedNotes });
      }
    } finally {
      this.closePostponeConfirm();
    }
  };


  isScheduleLocked = () => {
    return this.areScheduleFieldsLocked();
  };

  isDischargedStatus = () => {
    const status =
      (this.props.surgery?.status || this.props.sxBooking?.status || "")
        .toString()
        .toLowerCase();
    return status === "discharged";
  };

  isCancelledStatus = () => {
    const status =
      (this.props.surgery?.status || this.props.sxBooking?.status || "")
        .toString()
        .toLowerCase();
    return status === "cancelled";
  };

  areScheduleFieldsLocked = () => {
    return Boolean(this.props.isScheduleLocked || this.isDischargedStatus() || this.isCancelledStatus());
  };

  notifyScheduleLocked = () => {
    const status = (this.props.surgery?.status || this.props.sxBooking?.status || "").toString().toLowerCase();
    const message = status === "cancelled"
      ? "Cannot change date/time for Cancelled surgery"
      : "Cannot change date/time for Discharged surgery";
    cogoToast.warn(message);
  };

  renderWithLock = (child, isLocked, opts = {}) => {
    const { fullWidth = false } = opts;
    return (
      <div
        style={{
          position: "relative",
          display: fullWidth ? "block" : "inline-block",
          width: fullWidth ? "100%" : "auto",
        }}
      >
        {child}
        {isLocked && (
          <div
            onClick={this.notifyScheduleLocked}
            style={{
              position: "absolute",
              inset: 0,
              cursor: "not-allowed",
              backgroundColor: "transparent",
            }}
          />
        )}
      </div>
    );
  };

  componentDidMount() {
    // Load existing IOL items when component mounts
    this.loadExistingIOLItems();
    this.ensureProcedureSummaryIsCurrent();
    if (this.props.allowPayorPanelOverride) {
      this.ensurePayorPanelNamesLoaded();
    }
  }

  componentDidUpdate(prevProps) {
    // Reload IOL items if the surgery booking data changes
    if (
      prevProps.sxBooking?.IOLitems !== this.props.sxBooking?.IOLitems ||
      // Also check if sxBooking was null/undefined before and now has data
      (!prevProps.sxBooking && this.props.sxBooking)
    ) {
      this.loadExistingIOLItems();
    }

    if (
      prevProps.sxBooking?.surgeries !== this.props.sxBooking?.surgeries
    ) {
      this.ensureProcedureSummaryIsCurrent();
    }

    if (!prevProps.allowPayorPanelOverride && this.props.allowPayorPanelOverride) {
      this.ensurePayorPanelNamesLoaded();
    }
  }

  ensurePayorPanelNamesLoaded = async () => {
    if (!this.props.allowPayorPanelOverride) return;
    if (this.state.isLoadingPayorPanelNames) return;
    if ((this.state.payorPanelNameItems || []).length > 0) return;
    const prefix = this.props.authStore?.prefix;
    if (!prefix) return;
    try {
      this.setState({ isLoadingPayorPanelNames: true });
      const response = await apiCallPost(
        `/v3/${prefix}/neoSxInsurancePanelMaster/getAll`,
        {}
      );
      const raw = response?.data || response || [];
      const arr = Array.isArray(raw) ? raw : [];
      const names = arr
        .map((item) => String(item.panelName || "").trim())
        .filter(Boolean);
      const unique = [...new Set(names)];
      const lower = new Set(unique.map((n) => n.toLowerCase()));
      if (!lower.has("cash")) unique.unshift("Cash");
      const curPanel = String(this.props.sxBooking?.payorPanel || "").trim();
      if (
        curPanel &&
        !unique.some((n) => n.toLowerCase() === curPanel.toLowerCase())
      ) {
        unique.push(curPanel);
      }
      unique.sort((a, b) => a.localeCompare(b));
      this.setState({ payorPanelNameItems: unique, isLoadingPayorPanelNames: false });
    } catch (e) {
      console.error("Failed to load insurance panels for payor override:", e);
      this.setState({
        payorPanelNameItems: ["Cash"],
        isLoadingPayorPanelNames: false,
      });
    }
  };

  // Load existing IOL items from props or backend
  loadExistingIOLItems = () => {
    try {
      // Check if IOL items exist in the surgery booking data
      const existingIOLItems = this.props.sxBooking?.IOLitems || [];

      if (existingIOLItems && existingIOLItems.length > 0) {
        // Map the backend data back to the component state format
        const mappedItems = existingIOLItems.map((iolItem) => ({
          _id: iolItem.itemId,
          itemName: iolItem.itemName,
          itemCategory: iolItem.itemCategory,
          hasVariants: iolItem.hasVariants,
          selectedVariants: iolItem.selectedVariants || {},
          variantSelectionComplete: iolItem.variantSelectionComplete || false,
          // Map item details back to the original structure
          numApplicableVariantProperties: iolItem.itemDetails?.numApplicableVariantProperties || 0,
          variantProperty1Name: iolItem.itemDetails?.variantProperty1Name || "",
          variantProperty1Values: iolItem.itemDetails?.variantProperty1Values || [],
          variantProperty2Name: iolItem.itemDetails?.variantProperty2Name || "",
          variantProperty2Values: iolItem.itemDetails?.variantProperty2Values || [],
          variantProperty3Name: iolItem.itemDetails?.variantProperty3Name || "",
          variantProperty3Values: iolItem.itemDetails?.variantProperty3Values || [],
        }));

        this.setState({
          selectedClinicalItems: mappedItems,
          isClinicalItemsDirty: false, // Not dirty since we just loaded from backend
        });
      }
    } catch (error) {
      console.error("Error loading existing IOL items:", error);
    }
  };

  // Handle item selection from dropdown - this now immediately saves
  handleItemSelect = async (item) => {
    if (item) {
      // Check if item is already selected
      const existingIndex = this.state.selectedClinicalItems.findIndex((existingItem) => existingItem._id === item._id);

      if (existingIndex === -1) {
        // Add the complete item with selected variants to the list
        const newSelectedItems = [
          ...this.state.selectedClinicalItems,
          item, // Item already contains selectedVariants and variantSelectionComplete
        ];

        this.setState({
          selectedClinicalItems: newSelectedItems,
          isClinicalItemsDirty: false, // Keep as false since we're saving immediately
        });

        // Immediately save the item to backend
        await this.saveIOLItemImmediately(item, newSelectedItems);

        cogoToast.success(`${item.itemName} added and saved successfully`);
      } else {
        cogoToast.warning("This item is already selected");
      }
    }
  };

  // Save IOL item immediately when added
  saveIOLItemImmediately = async (newItem, allSelectedItems) => {
    try {
      // Prepare clinical items data for backend
      const clinicalItemsData = allSelectedItems.map((item) => ({
        itemId: item._id,
        itemName: item.itemName,
        itemCategory: item.itemCategory,
        hasVariants: item.hasVariants,
        selectedVariants: item.selectedVariants || {},
        variantSelectionComplete: item.variantSelectionComplete || false,
        // Include all item details for reference
        itemDetails: {
          numApplicableVariantProperties: item.numApplicableVariantProperties,
          variantProperty1Name: item.variantProperty1Name,
          variantProperty1Values: item.variantProperty1Values,
          variantProperty2Name: item.variantProperty2Name,
          variantProperty2Values: item.variantProperty2Values,
          variantProperty3Name: item.variantProperty3Name,
          variantProperty3Values: item.variantProperty3Values,
        },
      }));

      // Prepare payload for the existing update API
      const updatePayload = {
        // Include existing clinical details fields
        anesthesia: this.props.sxBooking.anesthesia,
        dietPreference: this.props.sxBooking.diet,
        diet: this.props.sxBooking.diet,
        isDiabetic: this.props.sxBooking.diabetic,
        diabetic: this.props.sxBooking.diabetic,
        additionalNotes: this.props.sxBooking.notes,
        notes: this.props.sxBooking.notes,
        // Add clinical items as IOLitems
        IOLitems: clinicalItemsData,
      };

      // Use the same API endpoint as the parent component
      const response = await apiCallPatch(
        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.props.surgery?.id}`,
        updatePayload
      );

      if (!response.success) {
        throw new Error(response.message || "Failed to save IOL item");
      }

      // Notify parent to update in-memory state and cache immediately
      if (this.props.onIOLItemsChanged) {
        try {
          await this.props.onIOLItemsChanged(clinicalItemsData);
        } catch (e) {
          // non-blocking
        }
      }

      // Notify parent component if needed
      if (this.props.onSaveClinicalDetails) {
        await this.props.onSaveClinicalDetails({
          clinicalItems: allSelectedItems,
        });
      }

      // Create audit log for the added item
      await this.logIOLItemAudit("ADD", newItem);
    } catch (err) {
      console.error("Failed to save IOL item immediately:", err);
      cogoToast.error("Failed to save IOL item");

      // Revert the state change on error
      this.setState({
        selectedClinicalItems: this.state.selectedClinicalItems.filter((item) => item._id !== newItem._id),
        isClinicalItemsDirty: false,
      });
    }
  };

  // Log audit for IOL item changes
  logIOLItemAudit = async (iolAction, item, previousItem = null) => {
    try {
      const resourceId = this.props.surgery?.id;
      const resourceName = `Surgery - ${this.props.surgery?.patientName || "Unknown Patient"}`;
      if (!resourceId || !this.props.authStore?.prefix) return;

      let actionSlug = "";
      let metadata = {};
      let actionType = "UPDATE";

      if (iolAction === "ADD") {
        // Extract SPH and CYL from selectedVariants based on variant property names
        const getVariantValueByKeywords = (keywords) => {
          try {
            const maxProps = item.numApplicableVariantProperties || 5;
            for (let i = 1; i <= maxProps; i++) {
              const propName = (item[`variantProperty${i}Name`] || "").toLowerCase();
              if (!propName) continue;
              if (keywords.some((kw) => propName.includes(kw))) {
                return (item.selectedVariants || {})[`property${i}`] || "";
              }
            }
          } catch (e) {
            // noop
          }
          return "";
        };
        const sphVal = getVariantValueByKeywords(["sph", "sphere"]);
        const cylVal = getVariantValueByKeywords(["cyl", "cylinder"]);
        const details = [];
        if (sphVal) details.push(`SPH: ${sphVal}`);
        if (cylVal) details.push(`CYL: ${cylVal}`);
        const detailsSuffix = details.length ? ` (${details.join(", ")})` : "";

        actionSlug = `Added IOL: "${item.itemName}"${detailsSuffix}`;
        metadata = {
          fieldName: "IOLitems",
          changeType: "ADD",
          previousValue: "",
          newValue: item.itemName,
          itemDetails: {
            itemId: item._id,
            itemName: item.itemName,
            itemCategory: item.itemCategory,
            hasVariants: item.hasVariants,
            selectedVariants: item.selectedVariants || {},
            sph: sphVal || undefined,
            cyl: cylVal || undefined,
          },
        };
      } else if (iolAction === "REMOVE") {
        actionSlug = `Removed IOL: "${item.itemName}"`;
        metadata = {
          fieldName: "IOLitems",
          changeType: "REMOVE",
          previousValue: item.itemName,
          newValue: "",
          itemDetails: {
            itemId: item._id,
            itemName: item.itemName,
            itemCategory: item.itemCategory,
          },
        };
      }

      const entry = {
        resourceType: "SURGERY_BOOKING",
        resourceId,
        resourceName,
        actionType,
        actionSlug,
        actionMetadata: metadata,
      };

      await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`, [entry]);
    } catch (err) {
      console.error("IOL item audit log failed:", err);
    }
  };

  // Get variant property name for display
  getVariantDisplayName = (item, propertyKey) => {
    // Extract property index from key (e.g., "property1" -> 1)
    const match = propertyKey.match(/property(\d+)/);
    if (!match) return propertyKey;

    const index = parseInt(match[1]);
    const variantName = item[`variantProperty${index}Name`];

    return variantName || propertyKey;
  };

  // Remove item from clinical selection - this now immediately saves
  handleRemoveClinicalItem = async (itemId) => {
    const itemToRemove = this.state.selectedClinicalItems.find((item) => item._id === itemId);
    if (!itemToRemove) return;

    const newSelectedItems = this.state.selectedClinicalItems.filter((item) => item._id !== itemId);

    this.setState({
      selectedClinicalItems: newSelectedItems,
      isClinicalItemsDirty: false, // Keep as false since we're saving immediately
    });

    // Immediately save the removal to backend
    await this.removeIOLItemImmediately(itemToRemove, newSelectedItems);

    cogoToast.success(`${itemToRemove.itemName} removed and saved successfully`);
  };

  // Remove IOL item immediately when removed
  removeIOLItemImmediately = async (removedItem, remainingSelectedItems) => {
    try {
      // Prepare clinical items data for backend
      const clinicalItemsData = remainingSelectedItems.map((item) => ({
        itemId: item._id,
        itemName: item.itemName,
        itemCategory: item.itemCategory,
        hasVariants: item.hasVariants,
        selectedVariants: item.selectedVariants || {},
        variantSelectionComplete: item.variantSelectionComplete || false,
        // Include all item details for reference
        itemDetails: {
          numApplicableVariantProperties: item.numApplicableVariantProperties,
          variantProperty1Name: item.variantProperty1Name,
          variantProperty1Values: item.variantProperty1Values,
          variantProperty2Name: item.variantProperty2Name,
          variantProperty2Values: item.variantProperty2Values,
          variantProperty3Name: item.variantProperty3Name,
          variantProperty3Values: item.variantProperty3Values,
        },
      }));

      // Prepare payload for the existing update API
      const updatePayload = {
        // Include existing clinical details fields
        anesthesia: this.props.sxBooking.anesthesia,
        dietPreference: this.props.sxBooking.diet,
        diet: this.props.sxBooking.diet,
        isDiabetic: this.props.sxBooking.diabetic,
        diabetic: this.props.sxBooking.diabetic,
        additionalNotes: this.props.sxBooking.notes,
        notes: this.props.sxBooking.notes,
        // Update clinical items as IOLitems
        IOLitems: clinicalItemsData,
      };

      // Use the same API endpoint as the parent component
      const response = await apiCallPatch(
        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.props.surgery?.id}`,
        updatePayload
      );

      if (!response.success) {
        throw new Error(response.message || "Failed to remove IOL item");
      }

      // Notify parent to update in-memory state and cache immediately
      if (this.props.onIOLItemsChanged) {
        try {
          await this.props.onIOLItemsChanged(clinicalItemsData);
        } catch (e) {
          // non-blocking
        }
      }

      // Notify parent component if needed
      if (this.props.onSaveClinicalDetails) {
        await this.props.onSaveClinicalDetails({
          clinicalItems: remainingSelectedItems,
        });
      }

      // Create audit log for the removed item
      await this.logIOLItemAudit("REMOVE", removedItem);
    } catch (err) {
      console.error("Failed to remove IOL item immediately:", err);
      cogoToast.error("Failed to remove IOL item");

      // Revert the state change on error
      this.setState({
        selectedClinicalItems: [...remainingSelectedItems, removedItem],
        isClinicalItemsDirty: false,
      });
    }
  };

  // Format date for DateInput component
  formatDate = (date) => {
    if (!date) return "";
    return moment(date).format("DD/MM/YYYY");
  };

  // Format time for DateInput component
  formatTime = (time) => {
    if (!time) return "";

    // If it's already a Date object, format it
    if (time instanceof Date) {
      // Check if the date is valid
      if (isNaN(time.getTime())) {
        return "";
      }
      return moment(time).format("HH:mm");
    }

    // If it's a string, try to format it
    if (typeof time === "string") {
      try {
        // Handle different time formats
        if (/^\d{1,2}:\d{2}$/.test(time)) {
          return time;
        } else if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(time)) {
          return moment(time, "h:mm A").format("HH:mm");
        } else {
          // Try to parse as ISO string or other formats
          const parsed = moment(time);
          return parsed.isValid() ? parsed.format("HH:mm") : "";
        }
      } catch (error) {
        console.error("Error formatting time:", time, error);
        return "";
      }
    }

    return "";
  };

  // Parse date for DateInput component
  parseDate = (dateString) => {
    if (!dateString) return null;
    const parsed = moment(dateString, "DD/MM/YYYY");
    return parsed.isValid() ? parsed.toDate() : null;
  };

  // Parse time for DateInput component
  parseTime = (timeString) => {
    if (!timeString) return null;

    // If it's already a Date object, validate and return it
    if (timeString instanceof Date) {
      // Check if the date is valid
      if (isNaN(timeString.getTime())) {
        return null;
      }
      return timeString;
    }

    // If it's a string, try to parse it
    if (typeof timeString === "string") {
      try {
        // Handle different time formats
        let time = timeString;

        // If it's already in HH:mm format, use it directly
        if (/^\d{1,2}:\d{2}$/.test(timeString)) {
          time = timeString;
        }
        // If it's in HH:mm AM/PM format, convert to 24-hour
        else if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(timeString)) {
          time = moment(timeString, "h:mm A").format("HH:mm");
        }
        // If it's an ISO string, extract time part
        else if (timeString.includes("T")) {
          const parsed = moment(timeString);
          if (parsed.isValid()) {
            time = parsed.format("HH:mm");
          }
        }

        // Validate the time format
        if (!/^\d{1,2}:\d{2}$/.test(time)) {
          console.warn("Invalid time format:", timeString);
          return null;
        }

        // Parse hours and minutes to validate range
        const [hours, minutes] = time.split(":").map(Number);
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
          console.warn("Time out of range:", time);
          return null;
        }

        // Create a proper date object for the time picker using today's date
        const today = new Date();
        const parsed = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, 0);

        // Validate the created date
        if (isNaN(parsed.getTime())) {
          console.warn("Failed to create valid date for time:", time);
          return null;
        }

        return parsed;
      } catch (error) {
        console.error("Error parsing time:", timeString, error);
        return null;
      }
    }

    return null;
  };

  // Create detailed audit logs for each section before saving
  createDetailedAuditLogs = async (section, options = {}) => {
    try {
      const entries = [];
      const current = options.current || this.props.sxBooking || {};
      const original = options.original || this.props.originalSxBooking || {};
      const resourceId = this.props.surgery?.id;
      const resourceName = `Surgery - ${this.props.surgery?.patientName || "Unknown Patient"}`;

      const safe = (v) => (v === null || v === undefined ? "" : v);
      const display = (v, fallback = "Empty") => (v === null || v === undefined || v === "" ? fallback : v);

      const pushUpdate = (fieldName, prevVal, newVal, slug) => {
        const prev = safe(prevVal);
        const curr = safe(newVal);
        if (prev === curr) return;
        entries.push({
          resourceType: "SURGERY_BOOKING",
          resourceId,
          resourceName,
          actionType: "UPDATE",
          actionSlug: slug,
          actionMetadata: {
            fieldName,
            changeType: "UPDATE",
            previousValue: prev,
            newValue: curr,
          },
        });
      };

      if (section === "medicalTeam") {
        // Surgeon
        if ((original.surgeonId || original.surgeon) !== (current.surgeonId || current.surgeon)) {
          pushUpdate(
            "surgeonId",
            original.surgeonId || "",
            current.surgeonId || "",
            `Updated surgeon: "${display(original.surgeon)}" → "${display(current.surgeon)}"`
          );
        }
        // Secondary Surgeon
        if (
          (original.secondarySurgeonId || original.secondarySurgeon) !==
          (current.secondarySurgeonId || current.secondarySurgeon)
        ) {
          pushUpdate(
            "secondarySurgeonId",
            original.secondarySurgeonId || "",
            current.secondarySurgeonId || "",
            `Updated secondary surgeon: "${display(original.secondarySurgeon)}" → "${display(
              current.secondarySurgeon
            )}"`
          );
        }
        // Anaesthetist
        if ((original.anaesthetistId || original.anaesthetist) !== (current.anaesthetistId || current.anaesthetist)) {
          pushUpdate(
            "anaesthetistId",
            original.anaesthetistId || "",
            current.anaesthetistId || "",
            `Updated anaesthetist: "${display(original.anaesthetist)}" → "${display(current.anaesthetist)}"`
          );
        }
        // Advised By
        if ((original.advisedById || original.advisedBy) !== (current.advisedById || current.advisedBy)) {
          pushUpdate(
            "advisedById",
            original.advisedById || "",
            current.advisedById || "",
            `Updated advised by: "${display(original.advisedBy)}" → "${display(current.advisedBy)}"`
          );
        }
        // Chosen By
        pushUpdate(
          "chosenBy",
          original.chosenBy || "",
          current.chosenBy || "",
          `Updated chosen by: "${display(original.chosenBy)}" → "${display(current.chosenBy)}"`
        );
      }

      if (section === "schedule") {
        // Compare only the date part (YYYY-MM-DD) to decouple from time
        const prevDateStr = original.date ? moment(original.date).format("YYYY-MM-DD") : "";
        const currDateStr = current.date ? moment(current.date).format("YYYY-MM-DD") : "";
        const prevDateDisp = original.date ? moment(original.date).format("DD/MM/YYYY") : "Empty";
        const currDateDisp = current.date ? moment(current.date).format("DD/MM/YYYY") : "Empty";

        console.log("Date Audit Check:", {
          originalDate: original.date,
          currentDate: current.date,
          prevDateStr,
          currDateStr,
          isDifferent: prevDateStr !== currDateStr,
          skipDateAudit: options.skipDateAudit,
        });

        // Skip date audit if this is a reschedule (already logged separately)
        if (!options.skipDateAudit && prevDateStr !== currDateStr) {
          pushUpdate("scheduledDate", prevDateStr, currDateStr, `Updated date: "${prevDateDisp}" → "${currDateDisp}"`);
        }

        // Compare only the time part (HH:mm) to decouple from date
        const fmtTime = (t) => {
          if (!t) return "";
          if (t instanceof Date) {
            // Check if the date is valid
            if (isNaN(t.getTime())) return "";
            return moment(t).format("HH:mm");
          }
          if (typeof t === "string") {
            // If it's an ISO string or contains date/time info, parse and extract time
            const parsed = moment(t);
            if (parsed.isValid()) {
              return parsed.format("HH:mm");
            }
            // If it's already in HH:mm format, return as is
            return t;
          }
          return "";
        };
        const prevTime = fmtTime(original.time);
        const currTime = fmtTime(current.time);

        console.log("Time Audit Check:", {
          originalTime: original.time,
          currentTime: current.time,
          prevTime,
          currTime,
          isDifferent: prevTime !== currTime,
          bothEmpty: !prevTime && !currTime,
        });

        // Only log time change if there's an actual change and at least one value is not empty
        // This prevents false positives when both are empty or when times are identical
        if (prevTime !== currTime && (prevTime || currTime)) {
          pushUpdate(
            "scheduledTime",
            prevTime,
            currTime,
            `Updated time: "${display(prevTime)}" → "${display(currTime)}"`
          );
        }

        pushUpdate(
          "otTheatre",
          original.otTheatre || "",
          current.otTheatre || "",
          `Updated OT/Theatre: "${display(original.otTheatre)}" → "${display(current.otTheatre)}"`
        );
        pushUpdate(
          "room",
          original.room || "",
          current.room || "",
          `Updated room: "${display(original.room)}" → "${display(current.room)}"`
        );
      }

      if (section === "clinicalDetails") {
        pushUpdate(
          "laterality",
          original.laterality || "",
          current.laterality || "",
          `Updated laterality: "${display(original.laterality)}" → "${display(current.laterality)}"`
        );
        // anesthesia moved to overall clinical level
        pushUpdate(
          "anesthesia",
          original.anesthesia || "",
          current.anesthesia || "",
          `Updated anesthesia: "${display(original.anesthesia)}" → "${display(current.anesthesia)}"`
        );
        pushUpdate(
          "diet",
          original.diet || "",
          current.diet || "",
          `Updated diet: "${display(original.diet)}" → "${display(current.diet)}"`
        );
        const prevDiab = original.diabetic === true ? "Yes" : original.diabetic === false ? "No" : "Empty";
        const currDiab = current.diabetic === true ? "Yes" : current.diabetic === false ? "No" : "Empty";
        if ((original.diabetic ?? "") !== (current.diabetic ?? "")) {
          pushUpdate(
            "diabetic",
            String(original.diabetic ?? ""),
            String(current.diabetic ?? ""),
            `Updated diabetic: "${prevDiab}" → "${currDiab}"`
          );
        }

        // Clinical Items
        const prevClinicalItems = JSON.stringify(this.state.selectedClinicalItems || []);
        const currClinicalItems = JSON.stringify(current.clinicalItems || []);
        if (prevClinicalItems !== currClinicalItems) {
          pushUpdate("clinicalItems", prevClinicalItems, currClinicalItems, `Updated clinical items selection`);
        }
      }

      if (section === "notes") {
        pushUpdate(
          "notes",
          original.notes || "",
          current.notes || "",
          `Updated notes: "${display(original.notes)}" → "${display(current.notes)}"`
        );
      }

      if (entries.length > 0 && resourceId && this.props.authStore?.prefix) {
        await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`, entries);
        // Do not toast here; keep UX same as save
      }
    } catch (err) {
      // Do not block UX on audit errors
      console.error("Audit log failed:", err);
    }
  };

  // Log a single surgery-level audit entry
  logSurgeryAudit = async ({ actionType, actionSlug, metadata = {} }) => {
    try {
      const resourceId = this.props.surgery?.id;
      const resourceName = `Surgery - ${this.props.surgery?.patientName || "Unknown Patient"}`;
      if (!resourceId || !this.props.authStore?.prefix) return;
      const entry = {
        resourceType: "SURGERY_BOOKING",
        resourceId,
        resourceName,
        actionType,
        actionSlug,
        actionMetadata: metadata,
      };
      await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`, [entry]);
    } catch (err) {
      console.error("Audit log failed:", err);
    }
  };

  handleRemoveSurgery = async (index) => {
    if (this.isDischargedStatus() || this.isCancelledStatus()) {
      const statusLabel = this.isCancelledStatus() ? "Cancelled" : "Discharged";
      cogoToast.warn(`Cannot remove surgeries after ${statusLabel}`);
      return;
    }
    const surgeries = this.props.sxBooking?.surgeries || [];
    try {
      const removed = surgeries[index];
      if (removed) {
        await this.logSurgeryAudit({
          actionType: "DELETE",
          actionSlug: `Removed surgery: "${removed.printedName || removed.name || "Unknown Surgery"}"`,
          metadata: {
            fieldName: "selectedSurgeries",
            changeType: "DELETE",
            previousValue: removed.printedName || removed.name || removed.id || removed._id || "",
            newValue: "",
          },
        });
      }
    } catch (e) {
      // non-blocking
    }

    const remaining = surgeries.filter((_, idx) => idx !== index);
    this.updateProcedureSummaryField(remaining);
    if (this.props.onRemoveSurgery) {
      await this.props.onRemoveSurgery(index);
    }
  };

  promptRemoveSurgery = (index) => {
    if (this.isDischargedStatus() || this.isCancelledStatus()) {
      const statusLabel = this.isCancelledStatus() ? "Cancelled" : "Discharged";
      cogoToast.warn(`Cannot remove surgeries after ${statusLabel}`);
      return;
    }
    const surgeries = this.props.sxBooking?.surgeries || [];
    if (surgeries.length <= 1) {
      cogoToast.warn("Cannot delete the only surgery. Please add another surgery first.");
      return;
    }
    const target = surgeries[index];
    if (!target) return;

    this.setState({
      isRemoveSurgeryDialogOpen: true,
      surgeryToRemoveIndex: index,
      surgeryToRemove: target,
    });
  };

  closeRemoveSurgeryDialog = () => {
    if (this.state.isRemovingSurgery) return;
    this.setState({
      isRemoveSurgeryDialogOpen: false,
      surgeryToRemoveIndex: null,
      surgeryToRemove: null,
    });
  };

  confirmRemoveSurgery = async () => {
    const { surgeryToRemoveIndex } = this.state;
    if (surgeryToRemoveIndex === null) return;
    this.setState({ isRemovingSurgery: true });
    try {
      await this.handleRemoveSurgery(surgeryToRemoveIndex);
      // keep modal open until parent state / header reflects the change
      setTimeout(() => {
        this.setState({
          isRemoveSurgeryDialogOpen: false,
          surgeryToRemoveIndex: null,
          surgeryToRemove: null,
        });
      }, 150);
    } catch (err) {
      cogoToast.error("Failed to remove surgery");
      this.setState({ isRemovingSurgery: false });
      return;
    }
    this.setState({ isRemovingSurgery: false });
  };

  handleSurgeryLateralityChange = async (index, newEye) => {
    try {
      const surgeries = [...(this.props.sxBooking?.surgeries || [])];
      const prev = surgeries[index]?.laterality || surgeries[index]?.eye || "";
      surgeries[index] = {
        ...(surgeries[index] || {}),
        laterality: newEye,
        eye: newEye,
      };
      if (this.props.onUpdateSurgeries) {
        await this.props.onUpdateSurgeries(surgeries);
      }
      this.updateProcedureSummaryField(surgeries);
      const s = surgeries[index] || {};
      await this.logSurgeryAudit({
        actionType: "UPDATE",
        actionSlug: `Changed laterality for "${s.printedName || s.name || "Unknown Surgery"}": "${prev || "Empty"
          }" → "${newEye || "Empty"}"`,
        metadata: {
          fieldName: "laterality",
          changeType: "UPDATE",
          previousValue: prev,
          newValue: newEye,
        },
      });
    } catch (e) {
      console.error("Failed to update surgery laterality:", e);
    }
  };

  updateProcedureSummaryField = (surgeriesOverride = null) => {
    if (!this.props.onUpdateField) return;
    const list = Array.isArray(surgeriesOverride)
      ? surgeriesOverride
      : this.props.sxBooking?.surgeries || [];
    const summary = buildProcedureDischargeSummaryName(list);
    const existing =
      this.props.sxBooking?.procedureDischargeSummaryName || "";
    if (summary !== existing) {
      this.props.onUpdateField("procedureDischargeSummaryName", summary);
    }
  };

  ensureProcedureSummaryIsCurrent = () => {
    const existing = (this.props.sxBooking?.procedureDischargeSummaryName || "").trim();
    if (!existing) {
      this.updateProcedureSummaryField();
    }
  };

  formatLateralityLabel = (value) => {
    if (!value) return "";
    const map = {
      left: "Left Eye",
      right: "Right Eye",
      both: "Both Eyes",
    };
    const key = String(value || "").toLowerCase();
    return map[key] || value;
  };

  // handleSurgeryAnesthesiaChange removed - anesthesia moved to overall clinical level

  handleSaveMedicalTeam = async () => {
    await this.createDetailedAuditLogs("medicalTeam");
    if (this.props.onSaveMedicalTeam) await this.props.onSaveMedicalTeam();
    // Clear calendar cache when surgeon changes (affects which calendar to show)
    this.clearCalendarCache();
  };

  // Clear the calendar cache to force fresh data on next open
  clearCalendarCache = () => {
    try {
      if (typeof window !== "undefined") {
        console.log("🗑️ Clearing calendar cache after save");

        // Clear main calendar cache
        if (window.__neoCache && window.__neoCache.sxCalendar) {
          window.__neoCache.sxCalendar = {};
        }

        // Clear inflight requests cache
        if (window.__neoInflight && window.__neoInflight.sxCalendar) {
          window.__neoInflight.sxCalendar = {};
        }

        console.log("✅ Calendar cache cleared successfully");
      }
    } catch (err) {
      console.error("Error clearing calendar cache:", err);
    }
  };

  handleSaveSchedule = async (saveOptions) => {
    try {
      const safeOptions =
        saveOptions &&
          typeof saveOptions === "object" &&
          typeof saveOptions.preventDefault === "function"
          ? {}
          : saveOptions || {};

      if (this.isScheduleLocked()) {
        this.notifyScheduleLocked();
        return false;
      }

      const originalScheduledDate = this.props.surgery?.originalScheduledDate;

      // Compare with last-saved date (originalSxBooking) to detect if
      // the date actually changed in THIS save action.
      // Using surgery.originalScheduledDate (the first-ever date) caused
      // duplicates on repeated saves and false positives.
      const lastSavedDate = this.props.originalSxBooking?.date;
      const lastSavedDateStr = lastSavedDate ? moment(lastSavedDate).format("YYYY-MM-DD") : "";
      const currentDateStr = this.props.sxBooking?.date
        ? moment(this.props.sxBooking.date).format("YYYY-MM-DD")
        : "";
      const dateActuallyChanged = lastSavedDateStr !== currentDateStr && (lastSavedDateStr || currentDateStr);
      const originalStatus = (this.props.originalSxBooking?.status || this.props.surgery?.status || "")
        .toString()
        .toLowerCase();
      const currentStatus = (this.props.sxBooking?.status || "").toString().toLowerCase();
      const wasPostponed = originalStatus === "postponed";

      // Determine if the server-side "rescheduled" flag should be set
      // (compared with the original scheduled date, not last saved date)
      const shouldMarkRescheduled =
        originalScheduledDate && this.props.sxBooking?.date
          ? moment(originalScheduledDate).format("YYYY-MM-DD") !==
          moment(this.props.sxBooking.date).format("YYYY-MM-DD")
          : false;

      // Update the rescheduled flag in state if applicable
      if (shouldMarkRescheduled && this.props.onUpdateField) {
        this.props.onUpdateField("rescheduled", true);
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      const originalSnapshot = JSON.parse(
        JSON.stringify(this.props.originalSxBooking || {})
      );
      const currentSnapshot = JSON.parse(
        JSON.stringify(this.props.sxBooking || {})
      );

      // Save BEFORE audit entries so failed saves don't create audit logs
      const saved = this.props.onSaveSchedule
        ? await this.props.onSaveSchedule(safeOptions)
        : false;
      if (!saved) {
        return false;
      }

      if (dateActuallyChanged) {
        const lastSavedDateDisplay = lastSavedDate
          ? moment(lastSavedDate).format("DD/MM/YYYY")
          : "Empty";
        const currentDateDisplay = this.props.sxBooking?.date
          ? moment(this.props.sxBooking.date).format("DD/MM/YYYY")
          : "Empty";

        const rescheduleSlug = wasPostponed
          ? `Postponed to new date: "${currentDateDisplay}"`
          : `Surgery rescheduled: "${lastSavedDateDisplay}" → "${currentDateDisplay}"`;
        const rescheduleChangeType = wasPostponed ? "POSTPONE_RESCHEDULE" : "RESCHEDULE";

        // Create a single reschedule audit log entry
        await this.logSurgeryAudit({
          actionType: "RESCHEDULE_SURGERY",
          actionSlug: rescheduleSlug,
          metadata: {
            fieldName: "scheduledDate",
            changeType: rescheduleChangeType,
            previousValue: lastSavedDateStr,
            newValue: currentDateStr,
            previousStatus: originalStatus || undefined,
            newStatus: currentStatus || undefined,
            rescheduled: shouldMarkRescheduled,
            originalScheduledDate: originalScheduledDate,
            newScheduledDate: this.props.sxBooking?.date || null,
          },
        });

        cogoToast.info(`Surgery rescheduled from ${lastSavedDateDisplay} to ${currentDateDisplay}`);

        // Create audit logs for other schedule fields (time, OT, room) but NOT date
        await this.createDetailedAuditLogs("schedule", {
          skipDateAudit: true,
          original: originalSnapshot,
          current: currentSnapshot,
        });
      } else {
        // Date didn't change — create normal audit logs for all schedule fields
        await this.createDetailedAuditLogs("schedule", {
          original: originalSnapshot,
          current: currentSnapshot,
        });
      }

      // Clear calendar cache after successful save
      this.clearCalendarCache();
      return true;
    } catch (err) {
      console.error("Failed to save schedule:", err);
      cogoToast.error("Failed to save schedule");
      return false;
    }
  };

  handleSaveClinicalDetails = async () => {
    try {
      await this.createDetailedAuditLogs("clinicalDetails");

      // Prepare clinical items data for backend (IOL items are already saved immediately)
      const clinicalItemsData = this.state.selectedClinicalItems.map((item) => ({
        itemId: item._id,
        itemName: item.itemName,
        itemCategory: item.itemCategory,
        hasVariants: item.hasVariants,
        selectedVariants: item.selectedVariants || {},
        variantSelectionComplete: item.variantSelectionComplete || false,
        // Include all item details for reference
        itemDetails: {
          numApplicableVariantProperties: item.numApplicableVariantProperties,
          variantProperty1Name: item.variantProperty1Name,
          variantProperty1Values: item.variantProperty1Values,
          variantProperty2Name: item.variantProperty2Name,
          variantProperty2Values: item.variantProperty2Values,
          variantProperty3Name: item.variantProperty3Name,
          variantProperty3Values: item.variantProperty3Values,
        },
      }));

      // Prepare payload for the existing update API - only for clinical details, not IOL items
      const updatePayload = {
        // Include existing clinical details fields (anesthesia moved to overall clinical level)
        anesthesia: this.props.sxBooking.anesthesia,
        dietPreference: this.props.sxBooking.diet,
        diet: this.props.sxBooking.diet,
        isDiabetic: this.props.sxBooking.diabetic,
        diabetic: this.props.sxBooking.diabetic,
        additionalNotes: this.props.sxBooking.notes,
        notes: this.props.sxBooking.notes,
        // Include IOL items in case they need to be synced
        IOLitems: clinicalItemsData,
      };

      // Use the same API endpoint as the parent component
      const response = await apiCallPatch(
        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.props.surgery?.id}`,
        updatePayload
      );

      if (!response.success) {
        throw new Error(response.message || "Failed to update clinical details");
      }

      // Pass to parent component
      if (this.props.onSaveClinicalDetails) {
        await this.props.onSaveClinicalDetails({
          clinicalItems: this.state.selectedClinicalItems,
        });
      }

      cogoToast.success("Clinical details saved successfully");
    } catch (err) {
      console.error("Failed to save clinical details:", err);
      cogoToast.error("Failed to save clinical details");
    }
  };

  handleSaveNotes = async () => {
    await this.createDetailedAuditLogs("notes");
    if (this.props.onSave) await this.props.onSave();
  };

  // Calendar modal handlers
  handleOpenCalendarModal = (surgeonId = null) => {
    if (this.areScheduleFieldsLocked()) {
      this.notifyScheduleLocked();
      return;
    }
    // Clear cache before opening to ensure fresh data
    this.clearCalendarCache();

    this.setState((prevState) => ({
      isCalendarModalOpen: true,
      pendingSurgeonChange: surgeonId,
      selectedCalendarDate: null,
      selectedCalendarSlot: null,
      chosenTime: null,
      calendarKey: prevState.calendarKey + 1, // Increment to force fresh data load
    }));
  };

  handleCloseCalendarModal = () => {
    this.setState({
      isCalendarModalOpen: false,
      pendingSurgeonChange: null,
      selectedCalendarDate: null,
      selectedCalendarSlot: null,
      chosenTime: null,
    });
  };

  handleCalendarDateSelect = (date) => {
    this.setState({ selectedCalendarDate: date });
  };

  handleCalendarSlotSelect = (slot) => {
    this.setState({ selectedCalendarSlot: slot });
  };

  handleChosenTimeChange = (time) => {
    this.setState({ chosenTime: time });
  };

  isCurrentSurgeryBooking = (booking) => {
    const bookingIds = [
      booking?.id,
      booking?._id,
      booking?.bookingId,
      booking?.surgeryId,
      booking?.sxBookingId,
      booking?.neoSxBookingId,
    ]
      .filter(Boolean)
      .map(String);

    if (!bookingIds.length) {
      return false;
    }

    const currentIds = [
      this.props.surgery?.id,
      this.props.surgery?._id,
      this.props.sxBooking?.id,
      this.props.sxBooking?._id,
    ]
      .filter(Boolean)
      .map(String);

    return currentIds.some((id) => bookingIds.includes(id));
  };

  isScheduledTimeWithinSlot = (scheduledTime, slotTimeFrame) => {
    if (!scheduledTime || !slotTimeFrame) {
      return false;
    }
    const [slotStart, slotEnd] = String(slotTimeFrame).split(" - ");
    if (!slotStart || !slotEnd) {
      return false;
    }

    const slotStartMoment = moment(slotStart, ["hh:mm A", "HH:mm"], true);
    const slotEndMoment = moment(slotEnd, ["hh:mm A", "HH:mm"], true);
    if (!slotStartMoment.isValid() || !slotEndMoment.isValid()) {
      return false;
    }

    let scheduledMoment = moment(scheduledTime, ["hh:mm A", "HH:mm"], true);
    if (!scheduledMoment.isValid()) {
      scheduledMoment = moment(scheduledTime);
    }
    if (!scheduledMoment.isValid()) {
      return false;
    }

    return scheduledMoment.isSameOrAfter(slotStartMoment) && scheduledMoment.isBefore(slotEndMoment);
  };

  getLiveSlotBookedCount = async ({ date, doctorId, branchName, slotTimeFrame }) => {
    if (!date || !doctorId || !branchName || !slotTimeFrame) {
      return { ok: false, bookedCount: 0 };
    }

    try {
      const dateString = moment(date).format("YYYY-MM-DD");
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`, {
        doctorId,
        branchName,
        startDate: dateString,
        endDate: dateString,
        page: 1,
        limit: 1000,
        mode: "calendar",
      });

      const surgeriesRaw = response?.data?.items ? response.data.items : response || [];
      const surgeries = filterBookingsForSlotMetrics(surgeriesRaw);
      let bookedCount = 0;

      surgeries.forEach((booking) => {
        if (!booking || this.isCurrentSurgeryBooking(booking)) {
          return;
        }

        const bookingDate = booking?.scheduledDate
          ? moment(booking.scheduledDate).format("YYYY-MM-DD")
          : null;
        if (bookingDate && bookingDate !== dateString) {
          return;
        }

        if (booking.timeFrame) {
          if (booking.timeFrame === slotTimeFrame) {
            bookedCount += 1;
          }
          return;
        }

        if (booking.scheduledTime && this.isScheduledTimeWithinSlot(booking.scheduledTime, slotTimeFrame)) {
          bookedCount += 1;
        }
      });

      return { ok: true, bookedCount };
    } catch (error) {
      console.error("Failed live slot capacity validation:", error);
      return { ok: false, bookedCount: 0 };
    }
  };

  validateLiveSlotCapacityBeforeSave = async ({
    selectedCalendarDate,
    selectedCalendarSlot,
    doctorId,
    branchName,
  }) => {
    const slotTimeFrame = selectedCalendarSlot?.timeFrame;
    const maxAllowedFromSlot = Number(selectedCalendarSlot?.maxAllowed) || 0;
    const capacityFromSlot = Number(selectedCalendarSlot?.capacity) || 0;
    const maxAllowed = maxAllowedFromSlot > 0 ? maxAllowedFromSlot : capacityFromSlot > 0 ? capacityFromSlot + 5 : 0;

    if (!slotTimeFrame || maxAllowed <= 0) {
      cogoToast.error("Selected slot has no configured capacity.");
      return false;
    }

    const liveCountResult = await this.getLiveSlotBookedCount({
      date: selectedCalendarDate,
      doctorId,
      branchName,
      slotTimeFrame,
    });

    if (!liveCountResult.ok) {
      cogoToast.error("Unable to verify latest slot availability. Please retry.");
      return false;
    }

    if (liveCountResult.bookedCount >= maxAllowed) {
      cogoToast.error(
        `Selected slot is full (${liveCountResult.bookedCount}/${maxAllowed}). Please choose another slot.`
      );
      this.setState({ selectedCalendarSlot: null, chosenTime: null });
      return false;
    }

    return true;
  };

  handleClearMedicalField = async (fieldKey) => {
    try {
      if (typeof this.props.onClearMedicalTeamField === "function") {
        await this.props.onClearMedicalTeamField(fieldKey);
      }
    } catch (err) {
      console.error("Failed to clear medical team field:", err);
    }
  };

  handleConfirmCalendarSelection = async () => {
    if (this.areScheduleFieldsLocked()) {
      this.notifyScheduleLocked();
      return;
    }
    const { selectedCalendarDate, selectedCalendarSlot, chosenTime, pendingSurgeonChange } = this.state;

    if (!selectedCalendarDate) {
      cogoToast.error("Please select a date");
      return;
    }

    if (!selectedCalendarSlot) {
      cogoToast.error("Please select a time slot");
      return;
    }

    const doctorIdForValidation = pendingSurgeonChange || this.props.sxBooking?.surgeonId;
    const branchNameForValidation =
      this.props.sxBooking?.branchName ||
      this.props.authStore?.branchName ||
      this.props.sxBooking?.branchId ||
      this.props.authStore?.branchId;
    const canProceedWithSelection = await this.validateLiveSlotCapacityBeforeSave({
      selectedCalendarDate,
      selectedCalendarSlot,
      doctorId: doctorIdForValidation,
      branchName: branchNameForValidation,
    });
    if (!canProceedWithSelection) {
      return;
    }

    // Apply the surgeon change if it was pending
    if (pendingSurgeonChange && this.props.onSurgeonChange) {
      const selectedConsultant = this.props.consultants.find((c) => c.id === pendingSurgeonChange);
      if (selectedConsultant) {
        await this.props.onSurgeonChange(pendingSurgeonChange);
        await this.handleSaveMedicalTeam();
      }
    }

    // Update date and time
    if (this.props.onUpdateField) {
      const updateFieldAsync = (field, value) =>
        new Promise((resolve) => {
          this.props.onUpdateField(field, value, resolve);
        });

      // Use chosen time if available, otherwise use slot start time
      const timeToUse = chosenTime || selectedCalendarSlot.time;
      let parsedTime = null;
      if (timeToUse) {
        const timeMoment = moment(timeToUse, ["HH:mm", "hh:mm A"]);
        if (timeMoment.isValid()) {
          const today = new Date();
          parsedTime = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            timeMoment.hours(),
            timeMoment.minutes(),
            0
          );
        }
      }

      try {
        await updateFieldAsync("date", selectedCalendarDate);
        if (parsedTime) {
          await updateFieldAsync("time", parsedTime);
        }
        await updateFieldAsync("status", "confirmed");
        const saved = await this.handleSaveSchedule({ status: "confirmed" });
        if (!saved) {
          return;
        }
        cogoToast.success("Schedule updated successfully");
      } catch (err) {
        console.error("Failed to auto-save schedule:", err);
        cogoToast.error("Failed to update schedule. Please try again.");
        return;
      }
    }

    this.handleCloseCalendarModal();
  };

  handleSurgeonChangeWithCalendar = (surgeonId) => {
    if (this.areScheduleFieldsLocked()) {
      this.notifyScheduleLocked();
      return;
    }
    if (!surgeonId) {
      // If clearing surgeon, just update directly
      if (this.props.onSurgeonChange) {
        this.props.onSurgeonChange(surgeonId);
      }
      return;
    }

    // Open calendar modal to select new date/time
    this.handleOpenCalendarModal(surgeonId);
  };

  handleDateFieldClick = () => {
    if (this.areScheduleFieldsLocked()) {
      this.notifyScheduleLocked();
      return;
    }
    // Open calendar modal when date field is clicked
    const currentSurgeonId = this.props.sxBooking?.surgeonId;
    if (!currentSurgeonId) {
      cogoToast.warning("Please select a surgeon first");
      return;
    }
    this.handleOpenCalendarModal(null);
  };

  render() {
    const {
      sxBooking,
      consultants,
      isLoadingConsultants,
      isSaving,
      isSavingMedicalTeam,
      isSavingSchedule,
      isSavingClinicalDetails,
      isMedicalTeamDirty,
      isScheduleDirty,
      isClinicalDetailsDirty,
      isNotesDirty,
      onUpdateField,
      onSurgeonChange,
      onSecondarySurgeonChange,
      onAnaesthetistChange,
      onClearMedicalTeamField,
      onSurgeonDropdownClick,
      onPostponeSurgery,
      isPostponingSurgery,
      postponeDisabledReason,
      onAddSurgery,
      onRemoveSurgery,
      onSave,
      onSaveMedicalTeam,
      onSaveSchedule,
      onSaveClinicalDetails,
      isScheduleLocked,
    } = this.props;
    const { postponeNotes, postponeNotesError } = this.state;
    console.log("here", sxBooking);

    const isLocked = this.props.canEdit === false;
    const statusValue = (
      this.props.surgery?.status ||
      sxBooking?.status ||
      ""
    )
      .toString()
      .toLowerCase();
    const postponeHiddenStatuses = ["discharged", "postponed", "cancelled"];
    const shouldShowPostponeButton = !postponeHiddenStatuses.includes(
      statusValue
    );
    const isDischarged = this.isDischargedStatus();
    const isCancelled = this.isCancelledStatus();
    const canModifySurgeries = !isDischarged && !isCancelled;
    const scheduleFieldsLocked = this.areScheduleFieldsLocked();
    const anesthesiaLocked = isDischarged || isCancelled;
    const hasPostponeNotes = Boolean((postponeNotes || "").trim());
    const existingPostponementNotes =
      sxBooking?.postponementNotes || this.props.surgery?.postponementNotes || "";

    return (
      <div style={{ padding: "20px", paddingTop: "0px" }}>
        {isLocked && (
          <Callout
            icon="lock"
            intent="warning"
            style={{
              marginBottom: "15px",
              backgroundColor: "var(--bg-surface)",
              border: "1px solid var(--border-color)",
            }}
          >
            This booking is locked. Fields cannot be edited after discharge.
          </Callout>
        )}
        {this.props.allowPayorPanelOverride && isDischarged && !isCancelled && (
          <Callout
            icon="key"
            intent="primary"
            style={{ marginBottom: "15px" }}
          >
            <Text style={{ color: "var(--text-primary)" }}>
              Payor panel can be changed at any time for your account. Updates save immediately to the
              server.
            </Text>
          </Callout>
        )}

        {/* Surgeries Section */}
        <Card elevation={Elevation.ONE} style={{ padding: "15px", marginBottom: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <H4 style={{ margin: 0, color: "var(--text-primary)" }}>Surgeries</H4>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {canModifySurgeries && (
                <Button intent="primary" icon="plus" onClick={onAddSurgery}>
                  Add Surgery
                </Button>
              )}
              <Button
                minimal
                // icon="dollar"
                style={{
                  backgroundColor: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                  color: "var(--text-primary)",
                  fontSize: "12px",
                  padding: "6px 12px",
                  height: "auto",
                }}
              >
                Cash/Subsidy: {sxBooking?.cashSubsidy || "Not specified"}
              </Button>
              {this.props.allowPayorPanelOverride ? (
                <ControlGroup style={{ alignItems: "center", gap: 8 }}>
                  <Tag minimal style={{ color: "var(--text-secondary)" }}>
                    Payor Panel
                  </Tag>
                  {this.props.payorPanelOverrideSaving ? (
                    <>
                      <Spinner size={18} intent="primary" />
                      <Text
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Saving payor panel…
                      </Text>
                    </>
                  ) : null}
                  <SingleSelectWrapper
                    items={this.state.payorPanelNameItems}
                    selectedItem={sxBooking?.payorPanel || null}
                    placeholder={
                      this.state.isLoadingPayorPanelNames
                        ? "Loading panels..."
                        : this.props.payorPanelOverrideSaving
                          ? "Saving…"
                          : "Search payor panels..."
                    }
                    disabled={
                      this.state.isLoadingPayorPanelNames ||
                      Boolean(this.props.payorPanelOverrideSaving)
                    }
                    loading={this.state.isLoadingPayorPanelNames}
                    emptyMessage="No payor panels available"
                    onChange={(label) => {
                      if (typeof this.props.onPayorPanelCommit === "function") {
                        this.props.onPayorPanelCommit(label);
                      }
                    }}
                    width="280px"
                    noResultsText="No payor panels found"
                    inputProps={{
                      style: { minWidth: "200px" },
                    }}
                  />
                </ControlGroup>
              ) : (
                <Button
                  minimal
                  style={{
                    backgroundColor: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    color: "var(--text-primary)",
                    fontSize: "12px",
                    padding: "6px 12px",
                    height: "auto",
                  }}
                >
                  Payor Panel: {sxBooking?.payorPanel || "Not specified"}
                </Button>
              )}
            </div>
          </div>
          {(sxBooking.surgeries || []).length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                backgroundColor: "var(--bg-surface)",
                borderRadius: "8px",
                border: "2px dashed var(--border-color)",
              }}
            >
              <Text
                style={{
                  color: "var(--text-secondary)",
                  fontStyle: "italic",
                  fontSize: "16px",
                }}
              >
                No surgeries added yet. Click "Add Surgery" to add procedures.
              </Text>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
              {(sxBooking.surgeries || []).map((surgery, index) => (
                <Card
                  key={index}
                  elevation={Elevation.TWO}
                  style={{
                    padding: "20px",
                    backgroundColor: "var(--bg-surface)",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    transition: "all 0.2s ease",
                  }}
                  className="surgery-card"
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        minWidth: "40px",
                        textAlign: "center",
                      }}
                    >
                      🏥
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "var(--text-primary)",
                          marginBottom: "4px",
                          display: "block",
                        }}
                      >
                        {surgery.printedName}
                      </Text>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <Tag large intent="primary" style={{ fontSize: "14px" }}>
                          {surgery.group}
                        </Tag>
                        {/* {this.props.sxBooking?.cashSubsidy && (
             <Tag 
              large 
              minimal 
              style={{
               fontSize: "14px",
               padding: "6px 10px",
               backgroundColor: "#8A9BA8",
               color: "#FFFFFF",
              }}
             >
              💰 {this.props.sxBooking.cashSubsidy}
             </Tag>
            )} */}
                        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                          {this.renderWithLock(
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "13px",
                                  color: "var(--text-secondary)",
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                }}
                              >
                                Laterality
                              </span>
                              <HTMLSelect
                                value={surgery.laterality || surgery.eye || ""}
                                onChange={(e) => this.handleSurgeryLateralityChange(index, e.target.value)}
                                disabled={isDischarged || isCancelled}
                                style={{
                                  backgroundColor: "var(--bg-surface)",
                                  color: "var(--text-primary)",
                                  width: "220px",
                                  fontSize: "16px",
                                  textAlign: "center",
                                  cursor: (isDischarged || isCancelled) ? "not-allowed" : "pointer",
                                }}
                              >
                                <option value="left">Left Eye</option>
                                <option value="right">Right Eye</option>
                                <option value="both">Both Eyes</option>
                              </HTMLSelect>
                            </div>,
                            (isDischarged || isCancelled)
                          )}
                          {/* Anesthesia moved to Clinical Details section */}
                        </div>
                      </div>
                    </div>
                    {canModifySurgeries && (
                      <Button
                        minimal
                        icon="trash"
                        intent="danger"
                        onClick={() => this.promptRemoveSurgery(index)}
                        style={{
                          minWidth: "40px",
                          minHeight: "40px",
                        }}
                      />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        {/* Clinical Items Search (moved out of Clinical Details) */}
        <Card elevation={Elevation.ONE} style={{ padding: "12px", marginBottom: "15px" }}>
          <H4 style={{ margin: "0 0 8px 0", color: "var(--text-primary)", fontSize: "16px" }}>Select IOLs</H4>
          <fieldset disabled={isLocked} style={{ border: "none", padding: 0, margin: 0 }}>
          <ItemSelectionDropdown onItemSelect={this.handleItemSelect} placeholder="Search IOLs..." />

          {/* Display Selected Clinical Items */}
          {this.state.selectedClinicalItems.length > 0 && (
            <div style={{ marginTop: "10px" }}>
              <div
                style={{
                  marginBottom: "6px",
                  fontWeight: "600",
                  color: "var(--text-primary)",
                  fontSize: "14px",
                }}
              >
                Selected IOLs:
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {this.state.selectedClinicalItems.map((item) => (
                  <Card
                    key={item._id}
                    style={{
                      padding: "8px",
                      backgroundColor: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontWeight: "600",
                            color: "var(--text-primary)",
                          }}
                        >
                          {item.itemName}

                          {/* Variant status indicator */}
                          {item.hasVariants && (
                            <Tag
                              intent={item.variantSelectionComplete ? "success" : "primary"}
                              minimal
                              style={{ fontSize: "10px" }}
                            >
                              {item.variantSelectionComplete
                                ? "✓ Variants Selected"
                                : `Has ${item.numApplicableVariantProperties} Variants`}
                            </Tag>
                          )}
                        </div>

                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-secondary)",
                            marginTop: "2px",
                          }}
                        >
                          Category: {item.itemCategory}
                        </div>

                        {/* Display selected variants */}
                        {item.hasVariants && item.selectedVariants && Object.keys(item.selectedVariants).length > 0 && (
                          <div style={{ marginTop: "8px" }}>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "var(--text-primary)",
                              }}
                            >
                              Selected Variants:
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "5px",
                                flexWrap: "wrap",
                                marginTop: "4px",
                              }}
                            >
                              {Object.entries(item.selectedVariants).map(
                                ([key, value]) =>
                                  value && (
                                    <Tag key={key} minimal intent="primary" style={{ fontSize: "11px" }}>
                                      {/* Display variant name and value */}
                                      {this.getVariantDisplayName(item, key)}: {value}
                                    </Tag>
                                  )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {!isLocked && (
                      <Button
                        minimal
                        icon="cross"
                        onClick={() => this.handleRemoveClinicalItem(item._id)}
                        style={{ color: "var(--text-secondary)" }}
                      />
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
          </fieldset>
        </Card>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "20px",
          }}
        >
          {/* Surgeon Information */}
          <Card elevation={Elevation.ONE} style={{ padding: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <H4 style={{ margin: 0, color: "var(--text-primary)" }}>Medical Team</H4>
              {isMedicalTeamDirty && !isLocked && (
                <Button
                  intent="primary"
                  icon="floppy-disk"
                  onClick={this.handleSaveMedicalTeam}
                  loading={isSavingMedicalTeam}
                  disabled={isSavingMedicalTeam}
                  small
                >
                  {isSavingMedicalTeam ? "Saving..." : "Save"}
                </Button>
              )}
            </div>
            <fieldset disabled={isLocked} style={{ border: "none", padding: 0, margin: 0 }}>
            <FormGroup label="Surgeon *" labelFor="surgeon">
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <HTMLSelect
                  id="surgeon"
                  value={sxBooking.surgeonId || ""}
                  onChange={(e) => this.handleSurgeonChangeWithCalendar(e.target.value)}
                  onFocus={onSurgeonDropdownClick}
                  fill
                  style={{
                    backgroundColor: "var(--bg-surface)",
                    color: "var(--text-primary)",
                  }}
                  disabled={isLoadingConsultants}
                >
                  <option value="">{isLoadingConsultants ? "Loading consultants..." : "Select Surgeon"}</option>
                  {consultants.map((consultant) => (
                    <option key={consultant.id} value={consultant.id}>
                      {consultant.shortcode ? `${consultant.shortcode} | ` : ""}
                      {consultant.name} {consultant.specialization ? `(${consultant.specialization})` : ""}
                    </option>
                  ))}
                </HTMLSelect>
                <Button
                  minimal
                  icon="cross"
                  small
                  title="Clear selection"
                  onClick={() => this.handleClearMedicalField("surgeon")}
                  disabled={
                    !onClearMedicalTeamField ||
                    isLoadingConsultants ||
                    isSavingMedicalTeam ||
                    (!sxBooking.surgeonId && !sxBooking.surgeon)
                  }
                />
              </div>
            </FormGroup>

            <FormGroup label="Secondary Surgeon" labelFor="secondarySurgeon">
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <HTMLSelect
                  id="secondarySurgeon"
                  value={sxBooking.secondarySurgeonId || ""}
                  onChange={(e) => onSecondarySurgeonChange(e.target.value)}
                  onFocus={onSurgeonDropdownClick}
                  fill
                  style={{
                    backgroundColor: "var(--bg-surface)",
                    color: "var(--text-primary)",
                  }}
                  disabled={isLoadingConsultants}
                >
                  <option value="">{isLoadingConsultants ? "Loading consultants..." : "Select Secondary Surgeon"}</option>
                  {consultants.map((consultant) => (
                    <option key={consultant.id} value={consultant.id}>
                      {consultant.shortcode ? `${consultant.shortcode} | ` : ""}
                      {consultant.name} {consultant.specialization ? `(${consultant.specialization})` : ""}
                    </option>
                  ))}
                </HTMLSelect>
                <Button
                  minimal
                  icon="cross"
                  small
                  title="Clear selection"
                  onClick={() => this.handleClearMedicalField("secondarySurgeon")}
                  disabled={
                    !onClearMedicalTeamField ||
                    isLoadingConsultants ||
                    isSavingMedicalTeam ||
                    (!sxBooking.secondarySurgeonId && !sxBooking.secondarySurgeon)
                  }
                />
              </div>
            </FormGroup>

            <FormGroup label="Anaesthetist" labelFor="anaesthetist">
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <HTMLSelect
                  id="anaesthetist"
                  value={sxBooking.anaesthetistId || ""}
                  onChange={(e) => onAnaesthetistChange(e.target.value)}
                  onFocus={onSurgeonDropdownClick}
                  fill
                  style={{
                    backgroundColor: "var(--bg-surface)",
                    color: "var(--text-primary)",
                  }}
                  disabled={isLoadingConsultants}
                >
                  <option value="">{isLoadingConsultants ? "Loading consultants..." : "Select Anaesthetist"}</option>
                  {consultants.map((consultant) => (
                    <option key={consultant.id} value={consultant.id}>
                      {consultant.shortcode ? `${consultant.shortcode} | ` : ""}
                      {consultant.name} {consultant.specialization ? `(${consultant.specialization})` : ""}
                    </option>
                  ))}
                </HTMLSelect>
                <Button
                  minimal
                  icon="cross"
                  small
                  title="Clear selection"
                  onClick={() => this.handleClearMedicalField("anaesthetist")}
                  disabled={
                    !onClearMedicalTeamField ||
                    isLoadingConsultants ||
                    isSavingMedicalTeam ||
                    (!sxBooking.anaesthetistId && !sxBooking.anaesthetist)
                  }
                />
              </div>
            </FormGroup>

            <FormGroup label="Advised By *" labelFor="advisedBy">
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <HTMLSelect
                  id="advisedBy"
                  value={sxBooking.advisedById || ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    if (this.props.onAdvisedByChange) {
                      this.props.onAdvisedByChange(id);
                    } else {
                      onUpdateField("advisedById", id);
                    }
                  }}
                  onFocus={onSurgeonDropdownClick}
                  fill
                  style={{
                    backgroundColor: "var(--bg-surface)",
                    color: "var(--text-primary)",
                  }}
                  disabled={isLoadingConsultants}
                >
                  <option value="">{isLoadingConsultants ? "Loading consultants..." : "Select Advised By"}</option>
                  {consultants.map((consultant) => (
                    <option key={consultant.id} value={consultant.id}>
                      {consultant.shortcode ? `${consultant.shortcode} | ` : ""}
                      {consultant.name} {consultant.specialization ? `(${consultant.specialization})` : ""}
                    </option>
                  ))}
                </HTMLSelect>
                <Button
                  minimal
                  icon="cross"
                  small
                  title="Clear selection"
                  onClick={() => this.handleClearMedicalField("advisedBy")}
                  disabled={
                    !onClearMedicalTeamField ||
                    isLoadingConsultants ||
                    isSavingMedicalTeam ||
                    (!sxBooking.advisedById && !sxBooking.advisedBy)
                  }
                />
              </div>
            </FormGroup>

            <FormGroup label="Chosen By" labelFor="chosenBy">
              <HTMLSelect
                id="chosenBy"
                value={sxBooking.chosenBy}
                onChange={(e) => onUpdateField("chosenBy", e.target.value)}
                fill
                style={{
                  backgroundColor: "var(--bg-surface)",
                  color: "var(--text-primary)",
                }}
              >
                <option value="">Select</option>
                <option value="patient">Patient</option>
                <option value="counsellor">Counsellor</option>
              </HTMLSelect>
            </FormGroup>
            </fieldset>
          </Card>

          {/* Schedule Information */}
          <Card elevation={Elevation.ONE} style={{ padding: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <H4 style={{ margin: 0, color: "var(--text-primary)" }}>Schedule</H4>
              {isScheduleDirty && !isLocked &&
                this.renderWithLock(
                  <Button
                    intent="primary"
                    icon="floppy-disk"
                    onClick={() => this.handleSaveSchedule()}
                    loading={isSavingSchedule}
                    disabled={isSavingSchedule || scheduleFieldsLocked}
                    small
                  >
                    {isSavingSchedule ? "Saving..." : "Save"}
                  </Button>,
                  scheduleFieldsLocked
                )}
            </div>
            <fieldset disabled={isLocked} style={{ border: "none", padding: 0, margin: 0 }}>
            <FormGroup label="Date *" labelFor="date">
              {this.renderWithLock(
                <InputGroup
                  id="date"
                  value={sxBooking.date ? moment(sxBooking.date).format("DD/MM/YYYY") : ""}
                  readOnly
                  placeholder="Click to select date from calendar"
                  onClick={scheduleFieldsLocked ? undefined : this.handleDateFieldClick}
                  style={{
                    cursor: "pointer",
                    backgroundColor: "var(--bg-surface)",
                  }}
                  fill
                  disabled={scheduleFieldsLocked}
                />,
                scheduleFieldsLocked,
                { fullWidth: true }
              )}
            </FormGroup>

            <FormGroup label="Time *" labelFor="time">
              {this.renderWithLock(
                <DateInput
                  value={sxBooking.time}
                  onChange={(time) => {
                    if (scheduleFieldsLocked) {
                      this.notifyScheduleLocked();
                      return;
                    }
                    onUpdateField("time", time);
                  }}
                  format="HH:mm"
                  formatDate={this.formatTime}
                  parseDate={this.parseTime}
                  timePrecision={TimePrecision.MINUTE}
                  fill
                  disabled={scheduleFieldsLocked}
                />,
                scheduleFieldsLocked,
                { fullWidth: true }
              )}
            </FormGroup>

            <FormGroup label="OT/Theatre" labelFor="otTheatre">
              <InputGroup
                id="otTheatre"
                value={sxBooking.otTheatre}
                onChange={(e) => onUpdateField("otTheatre", e.target.value)}
                placeholder="Enter OT/Theatre"
                fill
              />
            </FormGroup>

            <FormGroup label="Room" labelFor="room">
              <InputGroup
                id="room"
                value={sxBooking.room}
                onChange={(e) => onUpdateField("room", e.target.value)}
                placeholder="Enter Room"
                fill
              />
            </FormGroup>

            {shouldShowPostponeButton && (
              <div
                style={{
                  marginTop: "12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: "6px",
                }}
              >
                {this.renderWithLock(
                  <Button
                    intent="none"
                    icon="time"
                    fill
                    onClick={() => {
                      if (!onPostponeSurgery) return;
                      this.openPostponeConfirm();
                    }}
                    loading={isPostponingSurgery}
                    title={postponeDisabledReason || undefined}
                    disabled={
                      !onPostponeSurgery ||
                      isPostponingSurgery ||
                      scheduleFieldsLocked ||
                      Boolean(postponeDisabledReason)
                    }
                    style={{
                      width: "100%",
                      backgroundColor: "var(--bg-surface)",
                      color: "var(--text-primary)",
                      border: "1px solid var(--border-color)",
                    }}
                  >
                    Postpone Sx
                  </Button>,
                  scheduleFieldsLocked,
                  { fullWidth: true }
                )}
                {postponeDisabledReason && !scheduleFieldsLocked && (
                  <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                    <Icon icon="lock" size={12} style={{ marginRight: 4 }} />
                    {postponeDisabledReason}
                  </span>
                )}
              </div>
            )}
            {existingPostponementNotes && (
              <Callout
                icon="comment"
                intent="warning"
                style={{
                  marginTop: "12px",
                  backgroundColor: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-color)",
                }}
                title="Postponement notes"
              >
                <Text
                  style={{
                    color: "var(--text-secondary)",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.4,
                  }}
                >
                  {existingPostponementNotes}
                </Text>
              </Callout>
            )}
            </fieldset>
          </Card>

          {/* Clinical Information */}
          <Card elevation={Elevation.ONE} style={{ padding: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <H4 style={{ margin: 0, color: "var(--text-primary)" }}>Clinical Details</H4>
              {isClinicalDetailsDirty && !isLocked && (
                <Button
                  intent="primary"
                  icon="floppy-disk"
                  onClick={this.handleSaveClinicalDetails}
                  loading={isSavingClinicalDetails}
                  disabled={isSavingClinicalDetails}
                  small
                >
                  {isSavingClinicalDetails ? "Saving..." : "Save"}
                </Button>
              )}
            </div>
            <fieldset disabled={isLocked} style={{ border: "none", padding: 0, margin: 0 }}>
            {/* Anesthesia moved from per-surgery to overall clinical level */}
            <FormGroup label="Anesthesia Type" labelFor="anesthesia">
              <HTMLSelect
                id="anesthesia"
                value={sxBooking.anesthesia || ""}
                onChange={(e) => onUpdateField("anesthesia", e.target.value)}
                style={{
                  backgroundColor: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  width: "100%",
                }}
                disabled={this.props.isLoadingAnesthesiaTypes || anesthesiaLocked}
                title={
                  anesthesiaLocked
                    ? "Anesthesia cannot be modified once the surgery is discharged"
                    : undefined
                }
              >
                {(this.props.anesthesiaTypes || []).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </HTMLSelect>
            </FormGroup>

            <FormGroup label="Diet" labelFor="diet">
              <InputGroup
                id="diet"
                value={sxBooking.diet}
                onChange={(e) => onUpdateField("diet", e.target.value)}
                placeholder="Enter Diet"
                fill
              />
            </FormGroup>

            <FormGroup label="Diabetic" labelFor="diabetic">
              <Switch
                id="diabetic"
                checked={sxBooking.diabetic}
                onChange={(e) => onUpdateField("diabetic", e.target.checked)}
                label="Patient is Diabetic"
              />
            </FormGroup>

            {/* Selected items moved to Clinical Items card above */}
            </fieldset>
          </Card>
        </div>

        {/* Notes Section */}
        <Card elevation={Elevation.ONE} style={{ padding: "15px", marginTop: "20px" }}>
          <H4 style={{ marginBottom: "15px", color: "var(--text-primary)" }}>Additional Notes</H4>
          <fieldset disabled={isLocked} style={{ border: "none", padding: 0, margin: 0 }}>
          <TextArea
            value={sxBooking.notes}
            onChange={(e) => onUpdateField("notes", e.target.value)}
            placeholder="Enter any additional notes..."
            fill
            rows={4}
            style={{
              backgroundColor: "var(--bg-surface)",
              color: "var(--text-primary)",
            }}
          />
          </fieldset>

          {isNotesDirty && !isLocked && (
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                intent="primary"
                icon="floppy-disk"
                onClick={this.handleSaveNotes}
                loading={isSaving}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </Card>

        {/* Calendar Modal for selecting date/time */}
        <Dialog
          isOpen={this.state.isCalendarModalOpen}
          onClose={this.handleCloseCalendarModal}
          title="Select Surgery Schedule"
          style={{
            width: "90vw",
            maxWidth: "1400px",
            paddingBottom: 0,
          }}
          className={Classes.DARK}
        >
          <div className={Classes.DIALOG_BODY} style={{ padding: "20px" }}>
            {/* Always show doctor information */}
            <div style={{ marginBottom: "16px" }}>
              {this.state.pendingSurgeonChange ? (
                <>
                  <Tag intent="primary" large>
                    Changing surgeon to:{" "}
                    {this.props.consultants.find((c) => c.id === this.state.pendingSurgeonChange)?.name || "Unknown"}
                  </Tag>
                  <Text style={{ marginTop: "8px", color: "var(--text-secondary)", display: "block" }}>
                    Please select a new date and time slot for this surgeon.
                  </Text>
                </>
              ) : (
                <>
                  <Tag intent="success" large>
                    Doctor:{" "}
                    {this.props.consultants.find((c) => c.id === this.props.sxBooking?.surgeonId)?.name ||
                      this.props.sxBooking?.surgeon ||
                      "Not selected"}
                  </Tag>
                  <Text style={{ marginTop: "8px", color: "var(--text-secondary)", display: "block" }}>
                    Select a date and time slot for this surgery.
                  </Text>
                </>
              )}
            </div>

            {/* Display current selection */}
            {(this.state.selectedCalendarDate || this.state.selectedCalendarSlot) && (
              <Card
                elevation={Elevation.TWO}
                style={{
                  marginBottom: "16px",
                  padding: "12px 16px",
                  backgroundColor: "var(--bg-surface)",
                }}
              >
                <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                  <Text style={{ fontWeight: "600", color: "var(--text-primary)" }}>Current Selection:</Text>
                  {this.state.selectedCalendarDate && (
                    <Tag intent="success" large>
                      📅 {moment(this.state.selectedCalendarDate).format("DD/MM/YYYY")}
                    </Tag>
                  )}
                  {this.state.selectedCalendarSlot && (
                    <Tag intent="success" large>
                      🕐 {this.state.selectedCalendarSlot.timeFrame}
                    </Tag>
                  )}
                  {this.state.chosenTime && (
                    <Tag intent="primary" large>
                      ⏰ {this.state.chosenTime}
                    </Tag>
                  )}
                </div>
              </Card>
            )}

            {/* Surgery Calendar Component */}
            <SurgeryCalendar
              key={`surgery-calendar-${this.state.calendarKey}`}
              doctorId={this.state.pendingSurgeonChange || this.props.sxBooking?.surgeonId}
              branchId={this.props.sxBooking?.branchId || this.props.authStore?.branchId}
              branchName={this.props.sxBooking?.branchName || this.props.authStore?.branchName}
              onDateSelect={this.handleCalendarDateSelect}
              onSlotSelect={this.handleCalendarSlotSelect}
              onChosenTimeChange={this.handleChosenTimeChange}
              chosenTime={this.state.chosenTime}
            />
          </div>

          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={this.handleCloseCalendarModal}>Cancel</Button>
              {this.renderWithLock(
                <Button
                  intent="primary"
                  onClick={this.handleConfirmCalendarSelection}
                  disabled={
                    !this.state.selectedCalendarDate ||
                    !this.state.selectedCalendarSlot ||
                    this.isScheduleLocked()
                  }
                >
                  Confirm Selection
                </Button>,
                this.isScheduleLocked()
              )}
            </div>
          </div>
        </Dialog>

        {/* Remove Surgery Confirmation */}
        <Dialog
          isOpen={this.state.isRemoveSurgeryDialogOpen}
          onClose={this.closeRemoveSurgeryDialog}
          title="Remove Surgery"
          className={Classes.DARK}
          style={{ width: "520px" }}
        >
          <div className={Classes.DIALOG_BODY} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {this.props.surgery?.isBilled && (
              <Callout intent="warning" icon="warning-sign" title="This surgery is currently marked as billed.">
                Removing a procedure will change the outstanding balance and automatically mark the booking as
                <strong> Not Billed</strong>. Billing will need to be finalized again after you make changes.
              </Callout>
            )}

            {this.state.surgeryToRemove && (
              <Card
                elevation={Elevation.ONE}
                style={{
                  backgroundColor: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                  padding: "12px",
                }}
              >
                <Text style={{ fontSize: "16px", fontWeight: 600, color: "var(--text-primary)" }}>
                  {this.state.surgeryToRemove.printedName || this.state.surgeryToRemove.name || "Surgery"}
                </Text>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                  {this.state.surgeryToRemove.group && (
                    <Tag minimal intent="primary">
                      {this.state.surgeryToRemove.group}
                    </Tag>
                  )}
                  {this.state.surgeryToRemove.laterality && (
                    <Tag minimal intent="none">
                      {this.formatLateralityLabel(this.state.surgeryToRemove.laterality)}
                    </Tag>
                  )}
                </div>
                <Text style={{ marginTop: "10px", color: "var(--text-secondary)" }}>
                  This will permanently remove the surgery and update consolidated billing.
                </Text>
              </Card>
            )}
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={this.closeRemoveSurgeryDialog} disabled={this.state.isRemovingSurgery}>
                Cancel
              </Button>
              <Button
                intent="danger"
                icon="trash"
                onClick={this.confirmRemoveSurgery}
                loading={this.state.isRemovingSurgery}
              >
                Remove Surgery
              </Button>
            </div>
          </div>
        </Dialog>

        <Dialog
          isOpen={this.state.showPostponeConfirm}
          onClose={this.closePostponeConfirm}
          title="Postpone Surgery"
          className={Classes.DARK}
          style={{ width: "520px" }}
        >
          <div
            className={Classes.DIALOG_BODY}
            style={{
              backgroundColor: "var(--bg-surface)",
              color: "var(--text-primary)",
            }}
          >
            <div style={{ marginBottom: "12px", color: "var(--text-primary)" }}>
              Postponing will clear the scheduled date/time and move this surgery
              to the postponed list. Continue?
            </div>
            <FormGroup
              label="Postponement notes (required)"
              labelFor="postponement-notes"
              helperText={postponeNotesError || undefined}
              intent={postponeNotesError ? "danger" : "none"}
              style={{ marginBottom: 0 }}
            >
              <TextArea
                id="postponement-notes"
                fill
                growVertically
                large
                placeholder="Enter the reason and any relevant details (max 2000 characters)"
                value={postponeNotes}
                onChange={this.handlePostponeNotesChange}
                maxLength={2000}
                style={{
                  backgroundColor: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  borderColor: postponeNotesError
                    ? "var(--color-danger, #db3737)"
                    : "var(--border-color)",
                }}
              />
            </FormGroup>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={this.closePostponeConfirm}>Cancel</Button>
              <Button
                intent="warning"
                icon="time"
                loading={isPostponingSurgery}
                disabled={isPostponingSurgery || !hasPostponeNotes}
                onClick={this.confirmPostponeSurgery}
              >
                Confirm Postpone
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
    );
  }
}

export default inject("authStore")(observer(SxBookingDetailsTab));

